// Adapted from :
//
//   https://gist.github.com/julianocomg/296469e414db1202fc86#gistcomment-1818845
//
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

import Affix from "../Utils/Affix";
import { NonDesktop } from "../Utils/ResponsiveQueries";
import { Provider as FixedActionButtonsContextProvider } from "./FixedActionButtonsContext";
import { useComponentIsUnmounted } from "../Utils/useComponentIsUnmounted";

const FixedActionButtonsManager = props => {
  const { children } = props;

  const isUnmounted = useComponentIsUnmounted();

  const [affixOffsetBottom, setAffixOffsetBottom] = useState(0);
  const [hasButtons, setHasButtons] = useState(false);
  const [placeholder, setPlaceholder] = useState();
  const [buttonsContainer, setButtonsContainer] = useState();

  const refreshContainerDisplayState = useCallback(() => {
    // We use setTimeout here to move the processing of the dimensions at the end of the
    // call stack, allowing React to render its sub component and process size
    // calculations before the size is actually calculated here.
    setTimeout(() => {
      // Ensure the component was not unmounted and that the placeholder DOM node is set
      // before running this async calculation callback
      if (isUnmounted() || !placeholder) return;

      const newHasButtons = buttonsContainer?.children?.length;
      const newAffixOffsetBottom = placeholder.offsetTop + placeholder.clientHeight;

      // If the button actions container added or removed the first button, we update
      // the current state's hasButton key.
      if (newHasButtons != hasButtons) {
        setHasButtons(newHasButtons);
      }

      // If the bottom offset changed, assign it to the new state
      if (newAffixOffsetBottom !== affixOffsetBottom) {
        setAffixOffsetBottom(newAffixOffsetBottom);
      }
    }, 0);
  }, [
    affixOffsetBottom,
    buttonsContainer?.children?.length,
    hasButtons,
    isUnmounted,
    placeholder
  ]);

  useEffect(() => {
    document.addEventListener("DOMContentLoaded", refreshContainerDisplayState);
    window.addEventListener("newContentLoaded", refreshContainerDisplayState);
    window.addEventListener("load", refreshContainerDisplayState);
    window.addEventListener("resize", refreshContainerDisplayState);
    window.addEventListener("scroll", refreshContainerDisplayState);

    return () => {
      document.removeEventListener("DOMContentLoaded", refreshContainerDisplayState);
      window.removeEventListener("newContentLoaded", refreshContainerDisplayState);
      window.removeEventListener("load", refreshContainerDisplayState);
      window.removeEventListener("resize", refreshContainerDisplayState);
      window.removeEventListener("scroll", refreshContainerDisplayState);
    };
  }, [refreshContainerDisplayState]);

  useEffect(() => {
    refreshContainerDisplayState();
  });

  const setFixedActionsPlaceholder = useCallback(
    newPlaceholder => {
      setPlaceholder(newPlaceholder);
      refreshContainerDisplayState();
    },
    [refreshContainerDisplayState]
  );

  const contextProviderValue = useMemo(
    () => ({
      setFixedActionsPlaceholder,
      buttonsContainer
    }),
    [buttonsContainer, setFixedActionsPlaceholder]
  );

  // Hide placeholder and the buttons container if there's no fixed action button
  if (placeholder) placeholder.style.height = hasButtons ? null : 0;

  const buttonsContainerClassName = classNames("fixed-button-actions-inner", {
    "fixed-button-actions-inner-empty": !hasButtons
  });

  return (
    <FixedActionButtonsContextProvider value={contextProviderValue}>
      {children}

      {placeholder &&
        createPortal(
          <NonDesktop>
            <Affix className="fixed-button-actions" offsetBottom={affixOffsetBottom}>
              <div className={buttonsContainerClassName} ref={setButtonsContainer}></div>
            </Affix>
          </NonDesktop>,
          placeholder
        )}
    </FixedActionButtonsContextProvider>
  );
};

export default FixedActionButtonsManager;
